import clsx from 'clsx';
import * as React from 'react';

import { MARK_DIFF_ADDED, MARK_DIFF_DELETED } from '@gitbook/doc-core';
import type * as hiveCore from '@gitbook/hive-core';
import { isRevisionPageDocument, PageDiffStatus } from '@gitbook/hive-core';
import type { Inline } from '@gitbook/slate';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { computeHaloLeftOutset, DIAGONAL_STRIPE_CLASSNAME } from './constants';
import { DiffContext } from './DiffContext';

// Idle frames before the split grid stops reporting itself busy. A measurement scheduled from a
// ResizeObserver only lands after the next frame's callbacks, so one idle frame is not enough.
const ALIGNMENT_IDLE_FRAMES = 2;

/**
 * Per-column split-view state: which side of the diff a subtree is rendering,
 * plus a cross-side block-height registry for aligning the two columns. Provided
 * separately for each column, so its value differs between the two.
 *
 * Not to be confused with `./DiffContext` (`useDiffContext`), the single
 * space-wide value describing *what* is being diffed (revisions + computed diffs).
 */
export type DiffSide = 'before' | 'after';

const DiffSideContext = React.createContext<DiffSide | null>(null);
const PanelFlashContext = React.createContext(false);
const DiffDecorationRootContext = React.createContext<React.RefObject<HTMLElement | null> | null>(
    null
);

/**
 * Provide the diff side to descendant renderers. Mount with `side={null}` in
 * stacked mode so the provider stays mounted and the slate editor instance is
 * preserved across toggles.
 */
export function DiffSideProvider(props: {
    side: DiffSide | null;
    children: React.ReactNode;
}): React.ReactElement {
    return <DiffSideContext.Provider value={props.side}>{props.children}</DiffSideContext.Provider>;
}

/**
 * Read the current diff side. Returns `null` when the diff is rendered stacked.
 */
export function useDiffSide(): DiffSide | null {
    return React.useContext(DiffSideContext);
}

/**
 * In a split-diff layout, Added marks belong only to the `after` side and
 * Deleted marks only to the `before` side, so each panel reads as its own
 * version of the paragraph.
 */
export function isDiffMarkHiddenOnSide(markType: string, side: DiffSide | null): boolean {
    if (!side) {
        return false;
    }
    if (side === 'before' && markType === MARK_DIFF_ADDED) {
        return true;
    }
    if (side === 'after' && markType === MARK_DIFF_DELETED) {
        return true;
    }
    return false;
}

/** Whether a void inline belongs to the other side of a split diff and must be hidden. */
export function useIsHiddenOnDiffSide(node: Inline): boolean {
    const diffSide = useDiffSide();
    if (!diffSide) {
        return false;
    }
    return node.getMarks().some((mark) => isDiffMarkHiddenOnSide(mark.type, diffSide));
}

/**
 * Tell descendant diff renderers that the panel around them is flashing to draw the eye, so the
 * things they already colour (block halos, item cells) can step one shade darker with it.
 */
export function PanelFlashProvider(props: {
    isFlashing: boolean;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <PanelFlashContext.Provider value={props.isFlashing}>
            {props.children}
        </PanelFlashContext.Provider>
    );
}

/** Whether the surrounding change panel is flashing. `false` outside a panel. */
export function usePanelFlash(): boolean {
    return React.useContext(PanelFlashContext);
}

/**
 * Provide the editor-root element that diff decorations are positioned against.
 */
export function DiffDecorationRootProvider(props: {
    rootRef: React.RefObject<HTMLElement | null>;
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <DiffDecorationRootContext.Provider value={props.rootRef}>
            {props.children}
        </DiffDecorationRootContext.Provider>
    );
}

/**
 * Read the editor-root element used by root-anchored diff decorations.
 */
export function useDiffDecorationRoot(): React.RefObject<HTMLElement | null> | null {
    return React.useContext(DiffDecorationRootContext);
}

/**
 * Split-view diff column alignment.
 *
 * In split view the before/after revisions render as two side-by-side columns
 * that must stay row-aligned even when a row exists on only one side, or differs
 * in height between the sides. Two techniques achieve this — which of the 2 we pick
 * is based on what kind of content we're aligning:
 *
 * Technique A — content ghosting (`useOtherSidePage` + `DiffSideGhost`):
 *   For page chrome (tags, cover, footer) where the difference is pure
 *   presence/absence and the other side's content is available to us as data.
 *   The side that's missing the element renders the OTHER side's real component
 *   invisibly (behind diagonal stripes), reserving exactly the right height.
 *   Exact and synchronous — no measurement.
 *
 * Technique B — height measurement (`SplitDiffColumnsProvider` /
 * `useBlockHeightSync` + `DiffHeightSpacer`):
 *   For editor content blocks. Both columns render the SAME diff document, so a
 *   block may be added/deleted (hidden on one side) OR modified (present on both
 *   sides but at DIFFERENT heights). A ghost can't express a per-side height
 *   delta, so instead each side reports its measured rendered height into a
 *   shared registry and the shorter side pads the difference. Handles all block
 *   diff kinds uniformly, at the cost of a registry + a RAF reconcile pass.
 *
 * Rule of thumb: if you already hold the other side's content and the difference
 * is just "present here / absent there", ghost it (A). If the content renders on
 * both sides and its height is side-specific, measure it (B).
 *
 * The two never overlap on the same element: only `BlockDiff` registers with the
 * height registry, so ghosted chrome (A) is never measured — picking A doesn't
 * also pay B's ResizeObserver + reconcile cost.
 *
 * Also here: `useDiffSide` / `DiffSideProvider`, the shared "which column am I"
 * context both techniques build on. Not to be confused with `./DiffContext`
 * (`useDiffContext`) — the space-wide value describing *what* is being diffed.
 */

/**
 * Technique A — content ghosting.
 *
 * Returns the `Revision` rendered in the OTHER split column: the head/current
 * revision when this column is `before`, the base revision when it's `after`.
 * `null` in stacked mode or outside a diff context (standalone editor, previews).
 *
 * Use directly when a ghost needs the other side's revision object — e.g.
 * `PageFooterSlot` renders the other side's footer from its revision.
 * `useOtherSidePage` builds on this to resolve the other side's page document.
 */
export function useOtherSideRevision(): hiveCore.Revision | null {
    const diffSide = useDiffSide();
    // Non-throwing lookup: these hooks run in some non-Space contexts (standalone
    // editor, previews) where DiffContext isn't mounted, and those paths never
    // need a ghost render anyway.
    const diffContext = React.useContext(DiffContext);
    if (!diffSide || !diffContext) {
        return null;
    }
    const { baseRevision, currentRevision } = diffContext;
    return (diffSide === 'before' ? currentRevision : baseRevision) ?? null;
}

/**
 * Wherever we call this hook, we get the _other_ side's page document so split
 * view can keep page chrome aligned. For modified pages, this lets a column
 * ghost-render the opposite column's tags, cover, or footer when that chrome is
 * missing locally.
 *
 * Fully added/deleted pages are different: the editor should show the
 * added/removed-page side as empty instead of ghosting same-id page data from
 * the other revision. For those pages, the computed page diff status wins over
 * a same-id lookup. Also returns `null` in stacked mode, on group/link pages,
 * or when the other side has no document page.
 */
export function useOtherSidePage(
    page: hiveCore.RevisionPageDocument
): hiveCore.RevisionPageDocument | null {
    const diffSide = useDiffSide();
    const diffContext = React.useContext(DiffContext);
    const pageDiffStatus = diffContext?.diff?.diffs.get(page.id)?.type;
    const otherRevision =
        diffContext && diffSide
            ? diffSide === 'before'
                ? diffContext.currentRevision
                : diffContext.baseRevision
            : null;

    if (
        (diffSide === 'after' && pageDiffStatus === PageDiffStatus.Added) ||
        (diffSide === 'before' && pageDiffStatus === PageDiffStatus.Deleted)
    ) {
        return null;
    }

    const otherPage = otherRevision?.pages[page.id];
    if (!otherPage || !isRevisionPageDocument(otherPage)) {
        return null;
    }
    return otherPage;
}

/**
 * Render `children` as a height-preserving diagonal-stripe placeholder. Use to
 * keep split-view columns vertically aligned when content exists on one side
 * only (covers, tags, footer for added/removed pages). Pair with
 * `useOtherSidePage` to source the other side's content.
 */
export function DiffSideGhost({ children }: { children: React.ReactNode }) {
    return (
        <div aria-hidden className={clsx(DIAGONAL_STRIPE_CLASSNAME, 'pointer-events-none')}>
            <div className="invisible">{children}</div>
        </div>
    );
}

/**
 * Technique B — height measurement.
 *
 * Cross-side block-height registry for split view. Holds a shared map of
 * `blockKey → { before, after }` rendered heights so each `BlockDiff` can
 * render a trailing spacer of `max(0, otherSide − thisSide)`, keeping paired
 * lines aligned across columns.
 */
type HeightEntry = { before?: number; after?: number };

export type BlockHeightSyncContextValue = {
    report: (side: DiffSide, blockKey: string, height: number | null) => void;
    reportEstimated: (side: DiffSide, blockKey: string, height: number | null) => void;
    /** Returns the recorded heights for both sides of a block (or undefined if unmeasured). */
    getEntry: (blockKey: string) => HeightEntry | undefined;
    /** Updated whenever any height changes; consumers re-render. */
    version: number;
};

/**
 * Run a callback on the next frame and return its cancel function.
 */
export type ScheduleFrame = (callback: () => void) => () => void;

const BlockHeightSyncContext = React.createContext<BlockHeightSyncContextValue | null>(null);
// Separate from the height registry so scheduling doesn't re-render on every height change.
const AlignmentFrameContext = React.createContext<ScheduleFrame>(scheduleNextFrame);

/**
 * Shared hovered diff-row state — which registered block row the pointer is currently over, shared
 * across both split columns so hovering either side (or the gutter between them) is a single "row
 * hovered" state.
 */
export type DiffRowHoverValue = {
    hoveredKey: string | null;
    showHovered: (diffBlockKey: string) => void;
    clearHovered: (diffBlockKey: string) => void;
};

const DiffRowHoverContext = React.createContext<DiffRowHoverValue | null>(null);

/**
 * Create a hovered-row state. Used by {@link SplitDiffColumnsProvider} for the shared cross-column
 * state, and by `BlockDiffLayerProvider` as a per-editor fallback in stacked view / the plain editor
 * (where no split provider is mounted).
 */
export function useDiffRowHoverState(): DiffRowHoverValue {
    const [hoveredKey, setHoveredKey] = React.useState<string | null>(null);

    const showHovered = React.useCallback((diffBlockKey: string) => {
        setHoveredKey(diffBlockKey);
    }, []);

    const clearHovered = React.useCallback((diffBlockKey: string) => {
        setHoveredKey((current) => (current === diffBlockKey ? null : current));
    }, []);

    return { hoveredKey, showHovered, clearHovered };
}

/**
 * Read the shared cross-column hovered-row state, or `null` when no split provider is mounted
 * (stacked view / plain editor — each editor keeps its own local hover state).
 */
export function useSharedDiffRowHover(): DiffRowHoverValue | null {
    return React.useContext(DiffRowHoverContext);
}

/**
 * Coordinates the two split-diff columns: the cross-side block-height registry (so paired rows stay
 * aligned) and the shared hovered-row state (so hovering either column or the gutter lights both
 * sides). Mount once around the split grid; height and hover are kept as separate contexts so a
 * hover change doesn't re-render every height consumer.
 */
export function SplitDiffColumnsProvider(props: {
    /** The split grid, marked `aria-busy` while its rows are still being aligned. */
    gridRef: React.RefObject<HTMLElement | null>;
    children: React.ReactNode;
}): React.ReactElement {
    const { gridRef } = props;
    // Heights live in a ref (not state) to avoid a full re-render on every
    // measurement. We bump a `version` state instead, so consumers re-render
    // once per batched RAF tick rather than per height write.
    const heightsRef = React.useRef<Map<string, HeightEntry>>(new Map());
    const estimatedHeightsRef = React.useRef<Map<string, HeightEntry>>(new Map());
    const [version, setVersion] = React.useState(0);
    const cancelBumpRef = React.useRef<(() => void) | null>(null);
    const pendingFramesRef = React.useRef(0);
    const idleCheckRef = React.useRef<number | null>(null);

    // Set on the element directly rather than through state, so tracking it never re-renders.
    const markBusy = useEventCallback(() => {
        gridRef.current?.setAttribute('aria-busy', 'true');
        if (idleCheckRef.current != null) {
            window.cancelAnimationFrame(idleCheckRef.current);
        }

        let idleFrames = 0;
        const checkIdle = () => {
            idleFrames = pendingFramesRef.current > 0 ? 0 : idleFrames + 1;
            if (idleFrames >= ALIGNMENT_IDLE_FRAMES) {
                idleCheckRef.current = null;
                gridRef.current?.removeAttribute('aria-busy');
                return;
            }
            idleCheckRef.current = window.requestAnimationFrame(checkIdle);
        };
        idleCheckRef.current = window.requestAnimationFrame(checkIdle);
    });

    const scheduleFrame: ScheduleFrame = useEventCallback((callback: () => void) => {
        let settled = false;
        const settle = () => {
            if (settled) {
                return false;
            }
            settled = true;
            pendingFramesRef.current -= 1;
            return true;
        };

        pendingFramesRef.current += 1;
        markBusy();
        const frame = window.requestAnimationFrame(() => {
            if (settle()) {
                callback();
            }
        });

        return () => {
            if (settle()) {
                window.cancelAnimationFrame(frame);
            }
        };
    });

    const bumpSoon = React.useCallback(() => {
        if (cancelBumpRef.current != null) return;
        cancelBumpRef.current = scheduleFrame(() => {
            cancelBumpRef.current = null;
            setVersion((v) => v + 1);
        });
    }, [scheduleFrame]);

    const report = React.useCallback(
        (side: DiffSide, blockKey: string, height: number | null) => {
            const map = heightsRef.current;
            const existing = map.get(blockKey) ?? {};
            const next = { ...existing };
            if (height == null) {
                delete next[side];
            } else {
                if (existing[side] === height) return;
                next[side] = height;
            }
            if (next.before == null && next.after == null) {
                map.delete(blockKey);
            } else {
                map.set(blockKey, next);
            }
            bumpSoon();
        },
        [bumpSoon]
    );

    const reportEstimated = React.useCallback(
        (side: DiffSide, blockKey: string, height: number | null) => {
            const map = estimatedHeightsRef.current;
            const existing = map.get(blockKey) ?? {};
            const next = { ...existing };
            if (height == null) {
                delete next[side];
            } else {
                if (existing[side] === height) return;
                next[side] = height;
            }
            if (next.before == null && next.after == null) {
                map.delete(blockKey);
            } else {
                map.set(blockKey, next);
            }
            bumpSoon();
        },
        [bumpSoon]
    );

    const getEntry = React.useCallback((blockKey: string) => {
        const measured = heightsRef.current.get(blockKey);
        const estimated = estimatedHeightsRef.current.get(blockKey);
        const entry =
            measured || estimated
                ? {
                      before: measured?.before ?? estimated?.before,
                      after: measured?.after ?? estimated?.after,
                  }
                : undefined;
        return entry;
    }, []);

    // Each commit can move rows that the columns then re-measure, including the first one, before
    // any block has reported.
    React.useLayoutEffect(() => {
        markBusy();
    }, [markBusy, version]);

    React.useEffect(
        () => () => {
            cancelBumpRef.current?.();
            if (idleCheckRef.current != null) {
                window.cancelAnimationFrame(idleCheckRef.current);
            }
        },
        []
    );

    const heightValue = React.useMemo<BlockHeightSyncContextValue>(
        () => ({ report, reportEstimated, getEntry, version }),
        [report, reportEstimated, getEntry, version]
    );

    const hoverValue = useDiffRowHoverState();

    return (
        <AlignmentFrameContext.Provider value={scheduleFrame}>
            <BlockHeightSyncContext.Provider value={heightValue}>
                <DiffRowHoverContext.Provider value={hoverValue}>
                    {props.children}
                </DiffRowHoverContext.Provider>
            </BlockHeightSyncContext.Provider>
        </AlignmentFrameContext.Provider>
    );
}

/**
 * Returns the cross-side height sync API, or `null` if no provider is mounted
 * (which is the case in stacked view — there's only one column, no syncing).
 */
export function useBlockHeightSync(): BlockHeightSyncContextValue | null {
    return React.useContext(BlockHeightSyncContext);
}

/**
 * Schedule measurement work for the next frame. Inside a split grid the grid stays `aria-busy`
 * until the callback has run or been cancelled; elsewhere it is a plain animation frame.
 */
export function useScheduleAlignmentFrame(): ScheduleFrame {
    return React.useContext(AlignmentFrameContext);
}

/**
 * Pure height-only stripe spacer. Use when `SplitDiffColumnsProvider` reports a
 * measured height delta between this side and the other, and there's no
 * per-side content to ghost — just the gap that needs filling.
 *
 * Paints its diagonal fill against the editor decoration root and extends by
 * the same horizontal outsets as the diff halos, so nested spacers line up with
 * the surrounding root-anchored halos.
 * - like the halo layer, the fill is `-z-[1]`, so it paints behind (not over) block borders/content
 * - the visible fill uses the full measured height
 * - vertical halo bleed is handled by the halo layer, not by shrinking the spacer fill
 */
export function DiffHeightSpacer({ height }: { height: number }) {
    const diffSide = useDiffSide();
    const decorationRootRef = useDiffDecorationRoot();
    const scheduleFrame = useScheduleAlignmentFrame();
    const rootRef = React.useRef<HTMLDivElement | null>(null);
    const [stripeBounds, setStripeBounds] = React.useState<{ left: number; width: number } | null>(
        null
    );

    React.useLayoutEffect(() => {
        const root = rootRef.current;
        const decorationRoot = decorationRootRef?.current;
        if (!root || height <= 0) {
            return;
        }

        let cancelFrame: (() => void) | null = null;
        const measure = () => {
            const nextDecorationRoot = decorationRootRef?.current;
            if (!nextDecorationRoot) {
                setStripeBounds((current) => (current === null ? current : null));
                return;
            }

            const rootRect = root.getBoundingClientRect();
            const decorationRootRect = nextDecorationRoot.getBoundingClientRect();
            const next = {
                left: Math.round(decorationRootRect.left - rootRect.left),
                width: Math.round(decorationRootRect.width),
            };
            setStripeBounds((current) =>
                current?.left === next.left && current?.width === next.width ? current : next
            );
        };
        const measureSoon = () => {
            cancelFrame?.();
            cancelFrame = scheduleFrame(() => {
                cancelFrame = null;
                measure();
            });
        };

        measure();
        const observer = new ResizeObserver(measureSoon);
        observer.observe(root);
        if (decorationRoot) {
            observer.observe(decorationRoot);
        }
        window.addEventListener('resize', measureSoon);

        return () => {
            cancelFrame?.();
            observer.disconnect();
            window.removeEventListener('resize', measureSoon);
        };
    }, [decorationRootRef, height, scheduleFrame]);

    const leftOutset = computeHaloLeftOutset(diffSide);
    const stripeStyle = stripeBounds
        ? {
              left: stripeBounds.left - leftOutset,
              width: stripeBounds.width + leftOutset,
          }
        : {
              left: -leftOutset,
              width: `calc(100% + ${leftOutset}px)`,
          };
    if (height <= 0) {
        return null;
    }

    // Occupy and paint exactly `height`; column alignment depends on this spacer
    // being a direct visual representation of the measured cross-side height delta.
    return (
        <div
            ref={rootRef}
            className="relative w-full"
            style={{
                height,
                boxSizing: 'border-box',
            }}
        >
            <div
                aria-hidden
                className={clsx(
                    'absolute -z-[1]',
                    DIAGONAL_STRIPE_CLASSNAME,
                    'pointer-events-none'
                )}
                style={
                    {
                        top: 0,
                        bottom: 0,
                        ...stripeStyle,
                    } as React.CSSProperties
                }
            />
        </div>
    );
}

function scheduleNextFrame(callback: () => void): () => void {
    const frame = window.requestAnimationFrame(callback);
    return () => window.cancelAnimationFrame(frame);
}
